import { ArrowForward } from '@mui/icons-material';
import { FormControl, IconButton, MenuItem, StandardTextFieldProps, TextField } from '@mui/material';
import { isArray } from 'lodash';
import { useTranslation } from 'next-i18next';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Controller, ControllerRenderProps, FieldValues, useFormContext } from 'react-hook-form';
import { useCustomTranslations } from '../../../../../hooks/useCustomTranslations';
import { useLimitedRegistrationField } from '../../../../../hooks/useLimitedRegistrationField';
import { RegistrationField, TextValueOption } from '../../../../../interfaces/interfaces';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import { RegistrationFormFieldProps } from '../../RegistrationFormField';

interface TraditionalSelectFieldInterface extends Omit<RegistrationFormFieldProps, 'register' | 'watch'> {
  isRequired: (field: RegistrationField) => boolean;
  setValue?: (name: string, value: any) => void;
  watch?: any;
  textFieldSx?: any;
}

export const TraditionalSelectField = ({
  field: registrationField,
  defaultValue,
  disabled,
  fieldsTextColor,
  variant,
  requiredFieldIds,
  limitedValuesCount,
  isRequired,
  updateRequiredFieldIds,
  showFieldLabel,
  onChange,
  onBlur,
  textFieldSx,
}: TraditionalSelectFieldInterface) => {
  const { t } = useTranslation();
  const { ct } = useCustomTranslations();
  const { isFieldOptionDisabled } = useLimitedRegistrationField();
  const {
    watch,
    setValue,
    control,
    formState: { errors },
  } = useFormContext();
  const [selectValues, setSelectValues] = useState<{ isCustom: boolean; value: string }[]>([]);
  const [customContent, setCustomContent] = useState<string>();
  const selectRef = useRef<any>();

  const customOption = registrationField.textValueOptions.find((tvo) => tvo.isCustom);
  const event = useSelector((state) => state.common.event);

  const handleSelectValuesUpdate = () => {
    if (!watch?.(registrationField._id)) {
      setSelectValues([]);
      return;
    }

    const currentValue = watch(registrationField._id);
    if (!registrationField.canMultiSelect) {
      setValue(registrationField._id, currentValue);
      return;
    }

    const values: string[] = currentValue.split(',').filter(Boolean);
    const lastCustomValue = [...values].reverse().find((val) => !registrationField.textValueOptions.some((opt) => !opt.isCustom && opt.content === val));
    const standardValues = values
      .filter((val) => registrationField.textValueOptions.some((opt) => !opt.isCustom && opt.content === val))
      .map((val) => ({
        isCustom: false,
        value: val,
      }));
    const newSelectValues = [...standardValues];
    if (lastCustomValue) {
      newSelectValues.push({ isCustom: true, value: lastCustomValue });
    }

    setSelectValues(newSelectValues);

    const newFormValue = [...standardValues.map((v) => v.value), ...(lastCustomValue ? [lastCustomValue] : [])].join(',');

    if (newFormValue !== currentValue) {
      setValue(registrationField._id, newFormValue);
    }
  };

  const handleCustomContentUpdate = () => {
    if (!customContent) return;

    const customValue = selectValues.find((v) => v.isCustom);
    if (customValue?.value === customContent) return;

    setSelectValues((prev) => {
      const filtered = prev.filter((v) => !v.isCustom);
      return [...filtered, { isCustom: true, value: customContent }];
    });
  };

  const handleSelectChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: ControllerRenderProps<FieldValues, string>) => {
    const value = e.target?.value;
    if (value === 'customValue' && customContent) {
      if (onChange) {
        onChange(registrationField._id, customContent);
      }
      field.onChange(customContent);
    } else {
      if (onChange) {
        const newValue = Array.isArray(value) ? value.join(',') : value;
        onChange(registrationField._id, newValue);
      }

      if (isArray(value)) {
        field.onChange(value?.filter((value: string) => value).join());
      } else {
        field.onChange(value);
      }
    }

    updateRequiredFieldIds(
      new Map(requiredFieldIds).set(
        registrationField._id,
        registrationField.textValueOptions.find((o) => o.content === value)?.conditionalRequiredFields?.map((f) => (f._id ? f._id : f)) || []
      )
    );
  };

  const handleClose = () => {
    if (onBlur) onBlur(registrationField._id, watch(registrationField._id));
  };

  const renderMenuItem = (tvo: TextValueOption, index: number) => {
    const disabled = isFieldOptionDisabled(registrationField, tvo, limitedValuesCount);

    if (disabled) {
      return (
        <MenuItem key={`field-${registrationField._id}-tvo-option-${index}`} disabled value={tvo.content}>
          <div className="flex flex-row items-center w-full">
            <p>{ct(tvo.content, tvo)}</p>
            &nbsp;&nbsp;&nbsp;
            <p className="text-sm italic text-slate-700">{tvo.limitWarning || t('common:tooltip-signup-limited').toString()}</p>
          </div>
        </MenuItem>
      );
    }

    return (
      <MenuItem key={`field-${registrationField._id}-tvo-option-${index}`} disabled={disabled} value={tvo.content}>
        {registrationField.fieldType === 'salutation' ? t(`account:profilefields.salutation-${tvo.content}`) : ct(tvo.content, tvo)}
      </MenuItem>
    );
  };

  useEffect(() => {
    handleSelectValuesUpdate();
  }, [watch?.(registrationField._id)]);

  useEffect(() => {
    handleCustomContentUpdate();
  }, [customContent]);

  useEffect(() => {
    if (defaultValue && customOption) {
      const values = defaultValue.split(',').filter(Boolean);
      const standardValues = values
        .filter((val) => registrationField.textValueOptions.some((opt) => !opt.isCustom && opt.content === val))
        .map((val) => ({
          isCustom: false,
          value: val,
        }));

      const customValue = values.find((val) => !registrationField.textValueOptions.some((opt) => !opt.isCustom && opt.content === val));

      if (customValue) {
        setCustomContent(customValue);
        setSelectValues([...standardValues, { isCustom: true, value: customValue }]);
      } else {
        setSelectValues(standardValues);
      }
    }
  }, [defaultValue, customOption]);

  return (
    <Controller
      control={control}
      name={registrationField._id}
      key={registrationField._id}
      rules={{
        required: {
          value: isRequired(registrationField),
          message: t('form:fieldrequired'),
        },
      }}
      defaultValue={defaultValue || ''}
      render={({ field }) => {
        return (
          <FormControl fullWidth variant={variant}>
            <TextField
              inputRef={field.ref}
              name={field.name}
              onBlur={field.onBlur}
              select
              color="secondary"
              error={!!errors[registrationField._id]}
              helperText={errors[registrationField._id]?.message?.toString()}
              variant={variant as StandardTextFieldProps['variant']}
              label={showFieldLabel !== false && registrationField.label ? ct(registrationField.label, registrationField, 'labelTranslations') : ''}
              disabled={disabled}
              value={registrationField.canMultiSelect ? undefined : field.value}
              fullWidth
              id={`select-${registrationField._id}`}
              sx={{
                ...textFieldSx,
                color: 'blue',
                '.MuiInputBase-root': {
                  color: fieldsTextColor || event?.template.mainColor,
                },
                '.MuiSvgIcon-root ': {
                  fill: `${fieldsTextColor || event?.template.mainColor} !important`,
                },
              }}
              SelectProps={{
                onClose: () => {
                  handleClose();
                },
                ...(registrationField.canMultiSelect
                  ? {
                      multiple: registrationField.textValueOptions?.length > 1,
                      value: field.value?.toString()?.split(',') || [],
                    }
                  : {
                      displayEmpty: true,
                    }),
              }}
              onChange={(e) => handleSelectChange(e, field)}>
              <MenuItem key="empty" value="" disabled>
                {t('common:please-select')}
              </MenuItem>
              {registrationField.textValueOptions
                .slice()
                .filter((tvo) => !tvo.isCustom)
                .sort((a, b) => a.sortIndex - b.sortIndex)
                .map((tvo, index) => renderMenuItem(tvo, index))}
              {customOption && (
                <MenuItem key={`field-${registrationField._id}-tvo-custom-option`} style={{ padding: 0 }} value={'customValue'}>
                  <div className="flex flex-row justify-between px-4 w-full h-full" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    <TextField fullWidth defaultValue={customContent} label={customOption.content} onBlur={({ target }) => setCustomContent(target.value)} />
                    <IconButton
                      onClick={() => {
                        setValue?.(registrationField._id, customContent);
                        selectRef?.current?.click();
                      }}
                      color="primary"
                      size="large">
                      <ArrowForward />
                    </IconButton>
                  </div>
                </MenuItem>
              )}
              <MenuItem ref={selectRef} style={{ display: 'none' }} value={customContent}>
                {customContent}
              </MenuItem>
            </TextField>
          </FormControl>
        );
      }}
    />
  );
};
